import type { StationRole } from '@/objects/generated';
import { SavedMatrixSettingsV1 } from './SavedMatrixSettingsV1';
import { SavedMatrixSettingsV2 } from './SavedMatrixSettingsV2';
import { SettingsBase } from './SettingsBase';
import type { RacesGroupsCategoriesMap } from '@/utilities/RacesGroupsCategoriesMap';
import { MatrixSettings } from './MatrixSettings';
import type { CategoryId, GroupId, RaceContestantId, RaceId, StationRoleId } from '@/utilities/typealiases';
import { arrayDiff, arrayIntersect, range } from '@/utilities';
import { SavedMatrixSettingsV3 } from './SavedMatrixSettingsV3';
import {
    buildRaceKeyFromRaceIds,
    getRaceIdsFromRaceKey,
    getStationRoleCellIdentifierForStationRoleAndLap,
} from '@/components/views/Matrix/classes/MatrixHelper';
import type { RaceKey } from '@/components/views/Matrix/classes/IMatrixConfig';
import type { MatrixContestant } from '@/components/views/Matrix/classes/MatrixContestant';
import { buildStationRoleColumnLayoutForRaces } from '@/components/views/Matrix/classes/MatrixStationRoleToColumnLayout';

type AllSavedMatrixSettingsVersions = SavedMatrixSettingsV1 | SavedMatrixSettingsV2 | SavedMatrixSettingsV3;
const LatestSavedMatrixSettingsVersion = SavedMatrixSettingsV3;

/**
 * Wrapper class to hold the different versions of the MatrixSettings.
 * This is used to deserialize the settings and determine which version to use.
 * It also maps to and from the MatrixSettings object that we are actually using at runtime
 */
export class SavedMatrixSettings extends SettingsBase {
    static NAME_OF_CLASS = 'SavedMatrixSettings';

    private SavedSettings: AllSavedMatrixSettingsVersions;

    public constructor(val?: AllSavedMatrixSettingsVersions) {
        super();

        // The val parameter needs to be nullable since we are creating this object to call the deserialize method
        this.SavedSettings = val ?? new LatestSavedMatrixSettingsVersion();
    }

    public static FromMatrixSettings(
        matrixSettings: MatrixSettings,
        racesGroupsCategoriesMap: RacesGroupsCategoriesMap,
        stationRoles: Map<StationRoleId, StationRole>,
        matrixContestants: Map<RaceContestantId, MatrixContestant>,
    ): SavedMatrixSettings {
        const savedSettings = new SavedMatrixSettings();
        savedSettings.SavedSettings = LatestSavedMatrixSettingsVersion.FromMatrixSettings(
            matrixSettings,
            racesGroupsCategoriesMap,
            stationRoles,
            matrixContestants,
        );

        return savedSettings;
    }

    /**
     * Builds a MatrixSettings object from the saved settings and the current state of the event.
     * This will also handle backwards compatibility with older versions of the settings.
     * @param racesGroupsCategoriesMap The racesGroupsCategoriesMap for how the event looks like now
     * @param stationRoles All the stationroles for the event
     * @returns A MatrixSettings object
     */
    public GetMatrixSettings(
        racesGroupsCategoriesMap: RacesGroupsCategoriesMap,
        stationRoles: Map<StationRoleId, StationRole>,
        matrixContestants: Map<RaceContestantId, MatrixContestant>,
    ): MatrixSettings {
        let categoryMap = new Map<RaceKey, CategoryId[]>();
        let raceGroupMap = new Map<RaceKey, GroupId[]>();
        let stationRolesMap = new Map<RaceKey, string[]>();

        const maxLapPerRace = Array.from(matrixContestants.values()).reduce((acc, contestant) => {
            acc.set(contestant.RaceID, Math.max(acc.get(contestant.RaceID) ?? 1, contestant.MaxLaps));
            return acc;
        }, new Map<RaceId, number>());

        const getValidStationRoleColumnIdsForRaceIds = (raceIds: RaceId[]): string[] => {
            const layout = buildStationRoleColumnLayoutForRaces(raceIds, stationRoles, matrixContestants);
            return layout.map((col) => getStationRoleCellIdentifierForStationRoleAndLap(col.Roles));
        };

        if (this.SavedSettings instanceof SavedMatrixSettingsV1) {
            // In V1 we only have the selected options, so the best we can do is just use whatever is saved
            categoryMap = new Map(
                Array.from(this.SavedSettings.CategoryMap).map(([raceId, categoryIds]) => [buildRaceKeyFromRaceIds([raceId]), categoryIds]),
            );
            raceGroupMap = new Map(
                Array.from(this.SavedSettings.RaceGroupMap).map(([raceId, groupIds]) => [buildRaceKeyFromRaceIds([raceId]), groupIds]),
            );
            stationRolesMap = new Map(
                Array.from(this.SavedSettings.StationRolesMap).flatMap(([raceId, stationRoleIds]) =>
                    range(1, maxLapPerRace.get(raceId) ?? 1).map((lap) => [
                        buildRaceKeyFromRaceIds([raceId]),
                        stationRoleIds.map((stationRoleId) =>
                            getStationRoleCellIdentifierForStationRoleAndLap([{ StationRoleId: stationRoleId, Lap: lap }]),
                        ),
                    ]),
                ),
            );
        } else if (this.SavedSettings instanceof SavedMatrixSettingsV2) {
            // In V2 we have every option, with an enabled/disabled flag, so we can know if we have added any new options
            categoryMap = new Map<RaceKey, CategoryId[]>();
            this.SavedSettings.CategoryMap.forEach((categoryIds, raceId) => {
                // Check for new options
                const newCategories = arrayDiff(
                    Array.from(racesGroupsCategoriesMap.GetCategoryIDs([], raceId)),
                    categoryIds.map((cat) => cat.id),
                );

                // Add the options that are enabled in the saved settings
                categoryMap.set(buildRaceKeyFromRaceIds([raceId]), [
                    ...categoryIds.filter((cat) => cat.enabled).map((cat) => cat.id),
                    ...newCategories,
                ]);
            });

            raceGroupMap = new Map<RaceKey, GroupId[]>();
            this.SavedSettings.RaceGroupMap.forEach((groupIds, raceId) => {
                // Check for new options
                const newGroups = arrayDiff(
                    Array.from(racesGroupsCategoriesMap.GetGroupIDs(raceId)),
                    groupIds.map((group) => group.id),
                );

                // Add the options that are enabled in the saved settings
                raceGroupMap.set(buildRaceKeyFromRaceIds([raceId]), [
                    ...groupIds.filter((group) => group.enabled).map((group) => group.id),
                    ...newGroups,
                ]);
            });

            stationRolesMap = new Map<RaceKey, string[]>();
            this.SavedSettings.StationRolesMap.forEach((stationRoleIds, raceId) => {
                // Check for new options
                const newStationRoles = arrayDiff(
                    Array.from(stationRoles.values())
                        .filter((sr) => sr.RaceID === raceId || sr.RaceID === null)
                        .map((role) => role.ID),
                    stationRoleIds.map((role) => role.id),
                );

                // Add the options that are enabled in the saved settings
                stationRolesMap.set(buildRaceKeyFromRaceIds([raceId]), [
                    ...stationRoleIds
                        .filter((role) => role.enabled)
                        .flatMap((role) =>
                            range(1, maxLapPerRace.get(raceId) ?? 1).map((lap) =>
                                getStationRoleCellIdentifierForStationRoleAndLap([{ StationRoleId: role.id, Lap: lap }]),
                            ),
                        ),
                    // Add any new station roles
                    ...newStationRoles.flatMap((stationRoleId) =>
                        range(1, maxLapPerRace.get(raceId) ?? 1).map((lap) =>
                            getStationRoleCellIdentifierForStationRoleAndLap([{ StationRoleId: stationRoleId, Lap: lap }]),
                        ),
                    ),
                ]);
            });
        } else if (this.SavedSettings instanceof SavedMatrixSettingsV3) {
            // In V3 the keys are RaceKeys instead of RaceIds
            // And we have saved the stationrole columnIds instead of the stationrole ids, so we can just use them directly

            // Check if any of the synthetic races are invalid (if a race has been deleted)
            const allRaceIds = racesGroupsCategoriesMap.GetRaceIDs();
            const raceKeysToSkip = new Set<RaceKey>();
            // This both assigns to SyntheticRaces AND adds to the raceKeysToSkip. Slight abuse of a filter, but whatever
            this.SavedSettings.MatrixConfig.SyntheticRaces = this.SavedSettings.MatrixConfig.SyntheticRaces.filter((syntheticRace) => {
                if (arrayDiff(syntheticRace, allRaceIds).length === 0) {
                    return true;
                }

                raceKeysToSkip.add(buildRaceKeyFromRaceIds(syntheticRace));
                return false;
            });

            // Categories
            categoryMap = new Map<RaceKey, CategoryId[]>();
            this.SavedSettings.CategoryMap.forEach((categoryIds, raceKey) => {
                if (raceKeysToSkip.has(raceKey)) {
                    return;
                }
                const raceIds = getRaceIdsFromRaceKey(raceKey);

                const allCategoriesForRaceKey = raceIds.flatMap((raceId) => racesGroupsCategoriesMap.GetCategoryIDs([], raceId));

                // Check for new options
                const newCategories = arrayDiff(
                    allCategoriesForRaceKey,
                    categoryIds.map((cat) => cat.id),
                );

                // Add the options that are enabled in the saved settings
                categoryMap.set(raceKey, [...categoryIds.filter((cat) => cat.enabled).map((cat) => cat.id), ...newCategories]);
            });

            // Racegroups
            raceGroupMap = new Map<RaceKey, GroupId[]>();
            this.SavedSettings.RaceGroupMap.forEach((groupIds, raceKey) => {
                if (raceKeysToSkip.has(raceKey)) {
                    return;
                }
                const raceIds = getRaceIdsFromRaceKey(raceKey);

                const allGroupsForRaceKey = raceIds.flatMap((raceId) => racesGroupsCategoriesMap.GetGroupIDs(raceId));

                // Check for new options
                const newGroups = arrayDiff(
                    allGroupsForRaceKey,
                    groupIds.map((group) => group.id),
                );

                // Add the options that are enabled in the saved settings
                raceGroupMap.set(raceKey, [...groupIds.filter((group) => group.enabled).map((group) => group.id), ...newGroups]);
            });

            // Station roles
            stationRolesMap = new Map<RaceKey, string[]>();
            this.SavedSettings.StationRolesMap.forEach((stationRoleIds, raceKey) => {
                if (raceKeysToSkip.has(raceKey)) {
                    return;
                }
                const raceIds = getRaceIdsFromRaceKey(raceKey);

                const allStationRolesForRaceKey = getValidStationRoleColumnIdsForRaceIds(raceIds);
                const allSavedStationRoleIds = stationRoleIds.map((role) => role.id);
                const savedEnabledStationRoleIds = stationRoleIds.filter((role) => role.enabled).map((role) => role.id);

                // If the saved options contain IDs that no longer exist in the current layout, use the full current layout.
                const hasOutdatedSavedStationRoleIds = arrayDiff(allSavedStationRoleIds, allStationRolesForRaceKey).length > 0;
                if (hasOutdatedSavedStationRoleIds) {
                    stationRolesMap.set(raceKey, allStationRolesForRaceKey);
                    return;
                }

                // Keep enabled saved values and include newly added columns.
                const newStationRoles = arrayDiff(allStationRolesForRaceKey, allSavedStationRoleIds);
                stationRolesMap.set(raceKey, [...savedEnabledStationRoleIds, ...newStationRoles]);
            });
        }

        // Initalize the selections.
        // This prefills the matrixSettings with all the stationRoles, RaceGroups and Categories, unless we have saved values.
        // It also removes any invalid saved values. (someone might have deleted a category after the settings were saved for example)
        racesGroupsCategoriesMap.GetRaceIDs().forEach((raceID) => {
            const allStatioRoleIDsForRace = getValidStationRoleColumnIdsForRaceIds([raceID]);
            const allGroupIDsForRace = racesGroupsCategoriesMap.GetGroupIDs(raceID);
            const allCategoryIDsForRace = racesGroupsCategoriesMap.GetCategoryIDs(allGroupIDsForRace, raceID);

            const raceKey = buildRaceKeyFromRaceIds([raceID]);

            if (!stationRolesMap.has(raceKey)) {
                stationRolesMap.set(raceKey, allStatioRoleIDsForRace);
            } else {
                // This removes any invalid saved values
                stationRolesMap.set(raceKey, arrayIntersect(stationRolesMap.get(raceKey)!, allStatioRoleIDsForRace));
            }

            if (!raceGroupMap.has(raceKey)) {
                raceGroupMap.set(raceKey, allGroupIDsForRace);
            } else {
                // This removes any invalid saved values
                raceGroupMap.set(raceKey, arrayIntersect(raceGroupMap.get(raceKey)!, allGroupIDsForRace));
            }

            if (!categoryMap.has(raceKey)) {
                categoryMap.set(raceKey, allCategoryIDsForRace);
            } else {
                // This removes any invalid saved values
                categoryMap.set(raceKey, arrayIntersect(categoryMap.get(raceKey)!, allCategoryIDsForRace));
            }
        });

        return new MatrixSettings({
            categoryMap: categoryMap,
            raceGroupMap: raceGroupMap,
            stationRolesMap: stationRolesMap,
            matrixConfig: this.SavedSettings.MatrixConfig,
        });
    }

    public serialize(): string {
        if (!(this.SavedSettings instanceof LatestSavedMatrixSettingsVersion)) {
            throw new Error('SavedSettings is not an instance of the latest version. This should not be possible');
        }
        return this.SavedSettings.serialize();
    }

    public deserialize(json: string) {
        // Parse as pojo so we can read the version
        const tempParsed = JSON.parse(json);
        const version = tempParsed.Version ?? 1;

        switch (version) {
            case 1:
                this.SavedSettings = new SavedMatrixSettingsV1(new SavedMatrixSettingsV1().deserialize(json));
                break;
            case 2:
                this.SavedSettings = new SavedMatrixSettingsV2(new SavedMatrixSettingsV2().deserialize(json));
                break;
            case 3:
                this.SavedSettings = new SavedMatrixSettingsV3(new SavedMatrixSettingsV3().deserialize(json));
                break;
            default:
                throw new Error(`Unsupported version: ${version}`);
        }

        return this.SavedSettings;
    }
}
